import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import React from 'react';
import { useIntl } from 'react-intl';

import { formatMoney } from '../../../util/currency';
import { dateFromAPIToLocalNoon } from '../../../util/dates';
import { FormattedMessage } from '../../../util/reactIntl';
import { LINE_ITEM_NIGHT } from '../../../util/types';
import { feeInfoStyles, itemValueStyles, lineItemStyles } from './BookingBreakdown.styles';

// Same options as the booking period row above, so a run's dates read like a
// slice of it.
const dateRangeFormatOptions = {
  month: 'short',
  day: 'numeric',
};

const labelStyles = { flex: 1 };

/**
 * Annotates each night line item with the nights it covers.
 *
 * A camping-ground stay emits one `line-item/night` per contiguous price run in
 * chronological order, so run n starts where run n-1 ended. Both ends are
 * inclusive: a run of two nights ends on the second night, not on the following
 * checkout day (unlike the booking period row, which shows the checkout date).
 *
 * @param {Array} nightItems night line items in chronological order
 * @param {Date|null} bookingStartDate first night of the stay, at local noon
 * @returns {Array<{ item: Object, quantity: number, startDate: Date|null, lastNightDate: Date|null }>}
 */
const nightRunsWithDates = (nightItems, bookingStartDate) => {
  let nightsBefore = 0;

  return nightItems.map((item) => {
    const quantity = parseInt(item.quantity, 10);
    const nights = Number.isFinite(quantity) ? quantity : 0;
    const startDate = bookingStartDate
      ? moment(bookingStartDate).add(nightsBefore, 'days').toDate()
      : null;
    const lastNightDate =
      bookingStartDate && nights > 0
        ? moment(bookingStartDate)
            .add(nightsBefore + nights - 1, 'days')
            .toDate()
        : null;
    nightsBefore += nights;

    return { item, quantity, startDate, lastNightDate };
  });
};

/**
 * Renders the nightly base price of a booking — one row per night line item.
 *
 * Pitch listings and stays inside a single season have exactly one such item
 * and render a single row without dates, as before. A camping-ground stay that
 * crosses a season boundary has one item per price run; those get the nights
 * they cover and an explanatory note.
 *
 * @param {Object} props
 * @param {Array} props.lineItems line items to display, see adjustLineItemsForDisplay
 * @param {Object} props.transaction transaction (or estimated transaction) the booking dates come from
 * @param {'customer'|'provider'} props.userRole role the breakdown is rendered for
 */
const LineItemBasePriceMaybe = ({ lineItems, transaction, userRole }) => {
  const intl = useIntl();

  // A refunded booking carries a second, negative night item — sometimes
  // flagged as a reversal, sometimes not. LineItemUnitsRefundMaybe renders it
  // as the refund row further down, so it is not one of the price runs here.
  const unitPurchases = lineItems.filter(
    (item) =>
      item.code === LINE_ITEM_NIGHT &&
      item.includeFor.includes(userRole) &&
      !item.reversal &&
      item.lineTotal.amount >= 0
  );

  // Same source as the booking period row above, so the run dates line up with
  // the start date shown there.
  const { start, displayStart } = transaction.booking?.attributes || {};
  const bookingStart = displayStart || start;
  const bookingStartDate = bookingStart ? dateFromAPIToLocalNoon(bookingStart) : null;

  const rows = nightRunsWithDates(unitPurchases, bookingStartDate).filter(
    ({ quantity }) => !!quantity
  );

  // A single run is the whole stay — its dates are already the booking period
  // shown above, and there is no seasonal split to explain.
  const hasSeasonalRuns = rows.length > 1;

  return rows.length > 0 ? (
    <>
      {rows.map(({ item, quantity, startDate, lastNightDate }, index) => (
        <Box key={index} sx={lineItemStyles}>
          <Typography sx={labelStyles} variant="lineItemLabel">
            <FormattedMessage
              id="BookingBreakdown.baseUnitNight"
              values={{ unitPrice: formatMoney(intl, item.unitPrice), quantity }}
            />
            {hasSeasonalRuns && startDate && lastNightDate ? (
              <Typography component="span" ml={0.5} variant="lineItemMuted">
                {/* A one-night run collapses to that single date. */}
                {intl.formatDateTimeRange(startDate, lastNightDate, dateRangeFormatOptions)}
              </Typography>
            ) : null}
          </Typography>
          <Typography sx={itemValueStyles} variant="lineItemLabel">
            {formatMoney(intl, item.lineTotal)}
          </Typography>
        </Box>
      ))}

      {hasSeasonalRuns ? (
        <Box sx={feeInfoStyles}>
          <Typography variant="lineItemMuted">
            <FormattedMessage id="BookingBreakdown.seasonalPriceNote" />
          </Typography>
        </Box>
      ) : null}
    </>
  ) : null;
};

export default LineItemBasePriceMaybe;
